<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-va-intent-performance-page" v-loading="loading">
    <wy-page-modal ref="pageModal" v-if="showTable && hasPageData" :onClose="handlePageModalClose">
      <!-- <wy-va-optimization-details-undertrained v-if="selectedItem&&selectedItem.isUnderTrained" @click="entteredDetailsPage = true" ref="intentPerfDetailPage" :parent="module" :selectedItem="selectedItem" :clickEvent="clickEvent" /> -->
      <wy-va-optimization-intent-action-detail @click="entteredDetailsPage = true" ref="intentPerfDetailPage" :parent="module" :selectedItem="selectedItem" :clickEvent="clickEvent" />
    </wy-page-modal>

    <div class="wy-va-intent-performance">
      <div class="wy-va-opitization-main-content">
        <div class="wy-va-intent-performance-title">
          <div style="display: inline-block;">
            <span>Intent Performance</span>
            <el-tooltip placement="bottom" effect="dark">
              <div slot="content">
                Pulls together all of the data necessary to understand overall intent performance and provides<br />
                opportunities to prioritize and improve underperforming intents.
              </div>
              <img src="../../assets/icons/icon_info.svg" alt="" />
            </el-tooltip>
          </div>

          <el-button v-if="showTable && hasPageData" :disabled="disableExport" class="export-btn" round @click="handleExport">
            <div class="export-icon"></div>
            <span class="export-text">Export</span> 
          </el-button>
        </div>

        <div class="wy-va-opitization-chart-section-wrapper" v-if="hasPageData">
          <div class="wy-va-opitization-chart-section" v-if="isShowPieChartPanel && performanceMetricData">
            <!-- pie charts -->
            <div class="optimization-details">
              <table ref="pies" class="optimization-details-table" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td v-if="showNluMetric">
                    <wy-va-optimization-pie-chart
                      :showLQIntentsMetric="showLowQualityIntentsMetric"
                      :title="performanceMetricData.nlu[0].label"
                      :parent="module"
                      traceKey="pieNlu"
                      :traceList="performanceMetricData.nlu"
                      :titleToolTipContent="performanceMetricData.nlu[0].toolTipContent"
                      :targetKey="'nlu'"
                    />
                  </td>
                  <td v-if="showFalsePositiveMetric">
                    <wy-va-optimization-pie-chart
                      :showLQIntentsMetric="showLowQualityIntentsMetric"
                      :title="performanceMetricData.falsePositives[0].label"
                      :parent="module"
                      traceKey="pieFalsePositives"
                      :traceList="performanceMetricData.falsePositives"
                      :titleToolTipContent="performanceMetricData.falsePositives[0].toolTipContent"
                      :targetKey="'falsePositives'"
                    />
                  </td>
                  <td v-if="showMsgNotUnderstoodMetric">
                    <wy-va-optimization-pie-chart
                      :showLQIntentsMetric="showLowQualityIntentsMetric"
                      :title="performanceMetricData.messagesNotUnderstood[0].label"
                      :parent="module"
                      traceKey="pieMessagesNotUnderstood"
                      :traceList="performanceMetricData.messagesNotUnderstood"
                      :titleToolTipContent="performanceMetricData.messagesNotUnderstood[0].toolTipContent"
                      :targetKey="'messagesNotUnderstood'"
                    />
                  </td>
                  <td v-if="showLowQualityIntentsMetric">
                    <wy-va-optimization-pie-chart
                      :showLQIntentsMetric="showLowQualityIntentsMetric"
                      :title="performanceMetricData.lowQualityIntents[0].label"
                      :parent="module"
                      traceKey="pieLowQualityIntents"
                      :traceList="performanceMetricData.lowQualityIntents"
                      :titleToolTipContent="performanceMetricData.lowQualityIntents[0].toolTipContent"
                      :targetKey="'lowQualityIntents'"
                    />
                  </td>
                </tr>
              </table>
            </div>
            <!-- line charts -->
            <div class="chart optimizationTrendChart">
              <wy-va-optimization-line-chart v-if="haslineChartData" ref="optimizationChart" :data="optimizationChartData" />
            </div>
          </div>

          <div
            v-loading="loadTables"
            v-show="showTable"
            :class="{ 'wy-optimization-table-section': isShowPieChartPanel && performanceMetricData, 'wy-optimization-table-section full-size': !isShowPieChartPanel || !performanceMetricData }"
          >
            <wy-va-intent-performance-header ref="header" :controller="module" />
            <wy-table-list
              ref="table"
              :controller="module"
              :enableSelection="true"
              :enableSave="true"
              :rowClickEvent="handleRowClick"
              :defaultSort="{ name: 'prioritySortBy', type: 0 }"
              searchFieldName="intent"
            />
          </div>
        </div>
        <div v-else class="no-result-found">
          <div class="no-data"></div>
          <div class="no-data-title">{{ $t('noResultFound.title') }}</div>
          <div class="no-data-content">
            <div class="content-text">
              {{ $t('noResultFound.message') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import moment from 'moment';
import wyVaOptimizationPieChart from '../optimization/wy-va-optimization-pie-chart';
import wyVaOptimizationLineChart from '../optimization/wy-va-optimization-line-chart';
import WyVaIntentPerformanceHeader from './wy-va-intent-performance-header';
import WyTableList from '../widgets/wy-table-list';
import WyPageModal from '../widgets/wy-page-modal';
import ObjectsToCsv from 'objects-to-csv';
// import WyVaOptimizationDetailsUndertrainedPage from './wy-va-optimization-details-undertrained.vue';
import WyVaOptimizationIntentActionDetailPage from './wy-va-optimization-intent-action-detail';
import WyUtils from '../../library/wy-utils.js';

export default Vue.extend({
  name: 'wy-va-intent-performance-page',
  components: {
    'wy-va-optimization-pie-chart': wyVaOptimizationPieChart,
    'wy-va-optimization-line-chart': wyVaOptimizationLineChart,
    'wy-va-intent-performance-header': WyVaIntentPerformanceHeader,
    'wy-table-list': WyTableList,
    'wy-page-modal': WyPageModal,
    // 'wy-va-optimization-details-undertrained': WyVaOptimizationDetailsUndertrainedPage,
    'wy-va-optimization-intent-action-detail': WyVaOptimizationIntentActionDetailPage,
  },

  data() {
    return {
      module: this,
      loading: false,
      selectedItem: null,
      clickEvent: null,
      entteredDetailsPage: false,
      loadTables: false,
      initDate: null,
      hasData: true,

      showTable: false,
      showNluMetric: false,
      showFalsePositiveMetric: false,
      showMsgNotUnderstoodMetric: false,
      showLowQualityIntentsMetric: false,
      disableExport: false,

      optimizationChartData: null,
      performanceMetricData: null,

      savedIntentsEnabled: false,

      columns: [
        {
          label: '',
          type: 'star',
          name: 'index',
          customCell: 'wy-va-intent-performance-name-cell',
          selected: true,
          sortable: false,
          mandatory: true,
          group: ' ',
          minWidth: 20,
          align: 'center',
          fixed: true,
        },
        {
          label: 'Name',
          name: 'intent',
          mandatory: true,
          selected: false,
          sortable: true,
          group: '',
          align: 'left',
          minWidth: 408,
          maxWidth: 408,
          fixed: true,
          mainColumn: true,
          copyable: false,
          isEditTopicName: false,
          extendableField: true,
        },

        { label: 'Total', name: 'priority', selected: true, sortable: true, sortBy: 'prioritySortBy', group: 'Priority', align: 'center', minWidth: 100 },

        { label: 'Total', name: 'totalUtterances', selected: true, sortable: true, sortBy: 'totalUtterancesSortBy', group: 'Utterances', align: 'left', minWidth: 100 },

        { label: 'Total', name: 'totalIssues', selected: true, sortable: true, group: 'Issue', minWidth: 100, align: 'right' },
        //removed trendGraph for OC-1351 {label: "Trend", type: "html", name: "issuesTrendGraph", selected: true, sortable: false, group: 'Issue', minWidth: 140},
        { label: '% of total', name: 'issuesPercent', selected: false, sortable: true, group: 'Issue', sortBy: 'issuesPercent', minWidth: 120, suffix: '%', align: 'right' },
        {
          label: '% change',
          type: 'percentChange',
          name: 'issuesChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'issuesChangeWithoutAbs',
          group: 'Issue',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'issuesChangeTooltip',
          compareWith: 'totalIssuesCompare',
        },

        { label: 'Total', name: 'totalVolume', selected: true, sortable: true, group: 'Times Presented', minWidth: 100, align: 'right' },
        //removed trendGraph for OC-1351 {label: "Trend", type: "html", name: "volumeTrendGraph", selected: true, sortable: false, group: 'Times Presented', minWidth: 180},
        { label: '% of total', name: 'volumePercent', selected: false, sortable: false, group: 'Times Presented', sortBy: 'volumePercent', minWidth: 120, suffix: '%', align: 'right' },
        {
          label: '% change',
          type: 'percentChange',
          name: 'volumeChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'volumeChangeWithoutAbs',
          group: 'Times Presented',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'volumeChangeTooltip',
          compareWith: 'totalVolumeCompare',
        },

        { label: 'Score', name: 'totalConfidenceScore', selected: false, sortable: true, group: 'Confidence', minWidth: 100, align: 'right' },
        //removed trendGraph for OC-1351 {label: "Trend", type: "html", name: "confidenceScoreGraph", selected: true, sortable: false, group: 'Confidence', minWidth: 140},
        // {label: "% of times presented", name: "confidenceScorePercent", selected: true, sortable: true, group: 'Confidence Score', sortBy: 'confidenceScorePercent', minWidth: 170, suffix: "%", align:"right"},
        {
          label: '% change',
          type: 'percentChange',
          name: 'confidenceScoreChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'confidenceScoreChangeWithoutAbs',
          group: 'Confidence',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'confidenceScoreChangeTooltip',
          compareWith: 'totalVolumeCompare',
        },

        { label: 'Total', name: 'totalFalsePositives', selected: true, sortable: true, group: 'False Positives', minWidth: 100, align: 'right' },
        { label: '% of total', name: 'falsePositivesPercent', selected: false, sortable: true, group: 'False Positives', sortBy: 'falsePositivesPercent', minWidth: 120, suffix: '%', align: 'right' },
        {
          label: '% change',
          type: 'percentChange',
          name: 'falsePositivesChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'falsePositivesChangeWithoutAbs',
          group: 'False Positives',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'falsePositivesChangeTooltip',
          compareWith: 'totalFalsePositivesCompare',
        },

        { label: 'Total', name: 'totalDym', selected: false, sortable: true, group: 'DYM Candidates', minWidth: 100, align: 'right' },
        { label: '% of total', name: 'dymPercent', selected: false, sortable: true, group: 'DYM Candidates', sortBy: 'dymPercent', minWidth: 120, suffix: '%', align: 'right' },
        {
          label: '% change',
          type: 'percentChange',
          name: 'dymChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'dymChangeWithoutAbs',
          group: 'DYM Candidates',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'dymChangeTooltip',
          compareWith: 'totalDymCompare',
        },

        { label: 'Total', name: 'totalBotRepetitions', selected: true, sortable: true, group: 'Bot Repetitions', minWidth: 100, align: 'right' },
        { label: '% of total', name: 'botRepetitionsPercent', selected: false, sortable: true, group: 'Bot Repetitions', sortBy: 'botRepetitionsPercent', minWidth: 120, suffix: '%', align: 'right' },
        {
          label: '% change',
          type: 'percentChange',
          name: 'botRepetitionsChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'botRepetitionsChangeWithoutAbs',
          group: 'Bot Repetitions',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'botRepetitionsChangeTooltip',
          compareWith: 'totalBotRepetitionsCompare',
        },

        { label: 'Total', name: 'totalTrainingCandidates', selected: false, sortable: true, group: 'Training Candidates', minWidth: 100, align: 'right' },
        {
          label: '% of total',
          name: 'trainingCandidatesPercent',
          selected: false,
          sortable: true,
          group: 'Training Candidates',
          sortBy: 'trainingCandidatesPercent',
          minWidth: 120,
          suffix: '%',
          align: 'right',
        },
        {
          label: '% change',
          type: 'percentChange',
          name: 'trainingCandidatesChangePercent',
          selected: true,
          sortable: true,
          sortBy: 'trainingCandidatesChangeWithoutAbs',
          group: 'Training Candidates',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'trainingCandidatesChangeTooltip',
          compareWith: 'totalTrainingCandidatesCompare',
        },

        { label: 'Total', name: 'totalDuplicateUtterances', perms: [4141], selected: true, sortable: true, sortBy: 'totalDuplicateUtterancesSortBy', group: 'Similar Utterances', minWidth: 100, align: 'right' },
        {
          label: '% of total',
          name: 'duplicateUtterancesPercent',
          perms: [4141],
          selected: false,
          sortable: true,
          group: 'Similar Utterances',
          sortBy: 'duplicateUtterancesPercent',
          minWidth: 120,
          suffix: '%',
          align: 'right',
        },
        {
          label: '% change',
          type: 'percentChange',
          name: 'duplicateUtterancesChangePercent',
          perms: [4141],
          selected: true,
          sortable: true,
          sortBy: 'duplicateUtterancesChangeWithoutAbs',
          group: 'Similar Utterances',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'duplicateUtterancesChangeTooltip',
          compareWith: 'totalDuplicateUtterancesCompare',
        },

        { label: 'Total', name: 'totalInconsistentUtterances', perms: [4141], selected: true, sortable: true, sortBy: 'totalInconsistentUtterancesSortBy', group: 'Low Quality Utterances', minWidth: 100, align: 'right' },
        {
          label: '% of total',
          name: 'inconsistentUtterancesPercent',
          perms: [4141],
          selected: false,
          sortable: true,
          group: 'Low Quality Utterances',
          sortBy: 'inconsistentUtterancesPercent',
          minWidth: 120,
          suffix: '%',
          align: 'right',
        },
        {
          label: '% change',
          type: 'percentChange',
          name: 'inconsistentUtterancesChangePercent',
          perms: [4141],
          selected: true,
          sortable: true,
          sortBy: 'inconsistentUtterancesChangeWithoutAbs',
          group: 'Low Quality Utterances',
          minWidth: 150,
          suffix: '%',
          align: 'right',
          tooltipMessage: 'inconsistentUtterancesChangeTooltip',
          compareWith: 'totalInconsistentUtterancesCompare',
        },

        { label: 'Total', name: 'notAppearedChannelsCount', selected: false, sortable: true, group: 'Missing Channels', minWidth: 100, align: 'right', tooltipMessage: 'missingChannelsTooltip' },

        { label: 'Date', name: 'lastUpdatedDate', selected: true, sortable: true, sortBy: 'lastUpdatedDateSorting', group: 'Last Updated', minWidth: 100, align: 'right' },
      ],
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'currentPageInfo', 'config', 'tableModel', 'companyConfig', 'authUser', 'isLoggedIn']),

    haslineChartData() {
      return this.optimizationChartData && this.optimizationChartData.dateList.length > 0;
    },

    isShowPieChartPanel() {
      return this.showChartPanel && (this.showNluMetric || this.showFalsePositiveMetric || this.showMsgNotUnderstoodMetric || this.showLowQualityIntentsMetric);
    },

    hasPageData() {
      return this.hasData;
    },
  },

  watch: {
    loading: function(val) {
      setTimeout(() => {
        val ? (this.showTable = false) : (this.showTable = true);
      }, 300);
    },
  },

  methods: {
    ...mapMutations(['updateDataModelColumns']),
    ...mapActions(['addIntent', 'removeIntent', 'fetchOptimizationIntentPerformance', 'fetchOptimizationIntentPerformanceSummary']),

    async refreshTable() {
      this.$refs.table.refreshTable();
      // await this.handleFilterChange({ forceRefresh: false });
    },

    async handleSaveIntent(intent) {
      // replace span tags since search adds them
      intent.intent = intent.intent.replace(/<span\b[^>]*>(.*?)<\/span>/gi, "$1");

      const result = await this.addIntent(intent);
      if (result && result.status && result.status == 200) {
        intent.savedIntent = true;
      }
    },

    async handleRemoveSavedIntent(intent) {
      // replace span tags since search adds them
      intent.intent = intent.intent.replace(/<span\b[^>]*>(.*?)<\/span>/gi, "$1");

      const result = await this.removeIntent(intent);
      if (result && result.status && result.status == 200) {
        intent.savedIntent = false;
        if (this.savedIntentsEnabled) {
          this.tableModel.results = this.tableModel.results.filter((el) => el.intent != intent.intent);
        }
      }

      // if the last saved intent is removed, hide export button
      if (this.tableModel.results.length == 0) {
        this.disableExport = true;
      }
    },

    async handleExport() {
      const selectedColumns = this.columns
      let selectedRows = null;
      if(this.dataModel.keyword && this.dataModel.keyword!='') {
        let keyword = this.dataModel.keyword.trim().replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        selectedRows = this.tableModel.results.filter(item => {
            return new RegExp(keyword, 'i').test(item.intent.replace("$", "\\$"))
        });
      }else{
        selectedRows = this.tableModel.results;
      }
      let exportResult = [];
      let exportRow = {};
      let value = 'N/A';

      delete selectedColumns[0] // delete the index column

      selectedRows.map((row) => {
        exportRow['Starred'] = row.savedIntent ? 'Yes' : 'No'; // Add the new column and set its value

        selectedColumns.map((column) => {
          const columnName = `${column.group} ${column.label}`;
          const columnValue = row[column.name];

          if (columnName.includes('%')) {
            value = `${Number(columnValue).toLocaleString()}%`;
          } else if (typeof columnName == 'number') {
            value = Number(columnValue).toLocaleString();
          } else {
            // string type
            value = columnValue;
          }
          delete exportRow['Rank #'];
          delete exportRow['Volume Trend'];

          this.dataModel.columns.forEach((el) => {
            if (el.name == column.name && el.selected == true) {
              if (columnValue == null || columnValue == -1) {
                return (exportRow[columnName] = 'N/A');
              }
              else {
                if (typeof value == 'string') {
                  exportRow[columnName] = value.replace(/(<([^>]+)>)/gi, '');
                  if (exportRow[columnName].includes(',')) {
                    exportRow[columnName] = '"' + exportRow[columnName] + '"';
                  }
                } else {
                  exportRow[columnName] = value;
                }
              }
            }
          });
        });
        exportResult.push(exportRow);
        exportRow = {};
      });

      const csv = new ObjectsToCsv(exportResult);
      const csvContent = await csv.toString();

      let exportedFilenmae = `${this.authUser.company.name.replace(/ /g, '_')}_Intent_Performance_Table_${this.dataModel.dateStart}_to_${this.dataModel.dateEnd}.csv`;
      let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      let downloadLink = document.createElement('a');
      if (downloadLink.download !== undefined) {
        let url = URL.createObjectURL(blob);
        downloadLink.setAttribute('href', url);
        downloadLink.setAttribute('download', exportedFilenmae);
        downloadLink.style.visibility = 'hidden';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        const eventInfo = {
          number_of_exported_intents: exportResult.length,
          saved_filter_on: this.savedIntentsEnabled
        }
        this.$router.logAmplitudeEvent({eventName: "export_intents", eventInfo});
      }
    },

    handleTableColumnOpenAmplitude() {
      this.$router.logAmplitudeEvent({eventName: "intent_column_selector_opened"});
    },

    handleTableColumnApplyAmplitude(updateColumnsInfo) {
      const eventInfo = {
        changes_made: updateColumnsInfo.changes_made,
        all_selected: updateColumnsInfo.all_selected,
        columns_added: updateColumnsInfo.columns_added,
        columns_removed: updateColumnsInfo.columns_removed,
        columns_selected: updateColumnsInfo.columns_selected
      }
      this.$router.logAmplitudeEvent({eventName: "intent_column_selector_applied", eventInfo});
    },

    async handleFilterChange() {
      if (this.$refs.intentPerfDetailPage) {
        await this.$refs.intentPerfDetailPage.loadIntentActionTable();
      }
      if ((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.loading = true;

        if (this.showChartPanel) {
          this.optimizationRawIntentPerformation = await this.fetchOptimizationIntentPerformance();
          this.performanceMetricData = this.optimizationRawIntentPerformation.totalPerformanceMetricData;
          this.optimizationChartData = this.optimizationRawIntentPerformation.trendPerformanceMetricData;
        }

        setTimeout(() => {
          if (this.$refs.optimizationChart && this.optimizationChartData) {
            this.$refs.optimizationChart.buildChart(this.optimizationChartData);
          }
        }, 300);
        this.hasData = true;
        let isOK = await this.fetchOptimizationIntentPerformanceSummary();

        this.savedIntentsEnabled = false;
        if(this.$refs.header) {
          this.$refs.header.keyword = "";
        }
        this.dataModel.keyword = '';

        if (this.$refs.table && isOK) {
          this.hasData = this.tableModel.results && this.tableModel.results.length > 0;

          this.applyGenerateTooltipMsg();
          this.$refs.table.refreshTable();

          let findItem = null;
          if (this.selectedItem && this.tableModel.results && this.tableModel.results.length > 0) {
            this.tableModel.results.forEach(() => {
              // if(item.topicDisplayName == this.selectedItem.topicDisplayName) {
              //   findItem = item;
              // }
            });
          }

          if (this.$refs.intentPerfDetailPage) {
            this.$refs.intentPerfDetailPage.refreshUI(findItem);
          } else {
            // this.loading = false;
          }
        } else {
          this.loading = false;
          this.hasData = false;
        }

        if (this.tableModel.results.length == 0) {
          this.disableExport = true;
        } else {
          this.disableExport = false;
        }
      }
    },

    applyGenerateTooltipMsg() {
      this.tableModel.results.forEach((item) => {
        item.volumeChangeTooltip = this.generatTooltipsMsg(item.totalVolumeCompare, this.dataModel.dateRangeInDays, false);
        item.actionItemsChangeTooltip = this.generatTooltipsMsg(item.totalActionItemsCompare, this.dataModel.dateRangeInDays, false);
        item.numOfUtterancesChangeTooltip = this.generatTooltipsMsg(item.totalNumOfUtterancesCompare, this.dataModel.dateRangeInDays, false);
        item.confidenceScoreChangeTooltip = this.generatTooltipsMsg(item.totalConfidenceScoreCompare, this.dataModel.dateRangeInDays, false);
        
        item.issuesChangeTooltip = this.generatTooltipsMsg(item.totalIssuesCompare, this.dataModel.dateRangeInDays, false);
        item.falsePositivesChangeTooltip = this.generatTooltipsMsg(item.totalFalsePositivesCompare, this.dataModel.dateRangeInDays, false);
        item.dymChangeTooltip = this.generatTooltipsMsg(item.totalDymCompare, this.dataModel.dateRangeInDays, false);
        item.botRepetitionsChangeTooltip = this.generatTooltipsMsg(item.totalBotRepetitionsCompare, this.dataModel.dateRangeInDays, false);
        item.trainingCandidatesChangeTooltip = this.generatTooltipsMsg(item.totalTrainingCandidatesCompare, this.dataModel.dateRangeInDays, false);
        item.duplicateUtterancesChangeTooltip = this.generatTooltipsMsg(item.totalDuplicateUtterancesCompare, this.dataModel.dateRangeInDays, false);
        item.inconsistentUtterancesChangeTooltip = this.generatTooltipsMsg(item.totalInconsistentUtterancesCompare, this.dataModel.dateRangeInDays, false);

        item.missingChannelsTooltip = this.generatMissingChannelsTooltipsMsg(item.notAppearedChannels);
        if (item.lastUpdatedDate && item.lastUpdatedDate != '') {
          item.lastUpdatedDateSorting = moment(item.lastUpdatedDate, 'YYYY-MM-DD').format('X');
          item.lastUpdatedDate = WyUtils.formatDate(item.lastUpdatedDate, 'MMM DD, YYYY');
        }
      });
    },

    generatTooltipsMsg(compareValue, numOfDaysInRange, isPercentage) {
      let message = '';
      let percentage = '';
      if (isPercentage) {
        percentage = '%';
      }
      message = `Compared to ${compareValue + percentage} over the previous ${numOfDaysInRange} days.`;
      return message;
    },

    generatMissingChannelsTooltipsMsg(notAppearedChannels) {
      let message = '';
      if (notAppearedChannels.length > 0) {
        notAppearedChannels.forEach((item) => {
          message = message + item + '<br />';
        });
        return message;
      } else {
        return null;
      }
    },

    async handleRowClick(rowIndex, item) {
      if(!item.blackListed) {
        this.selectedItem = item;
        this.$store.dispatch('amplitudeLog', { name: 'intent_performance_action', data: { row_number: rowIndex + 1, name: item } });
        this.$refs.pageModal.open();
        this.$router.logAmplitudeEventSubPage("Intent Performance Individual Detail");
      }
    },

    handlePageModalClose() {
      this.$router.logAmplitudeEventSubPage();
    }
  },

  async created() {
    Vue.component('wy-va-intent-performance-name-cell', () => import('./wy-va-intent-performance-name-cell'));
    this.initDate = new Date();
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if (this[event.name]) this[event.name]();
    });

    let nluIntentPerformancePageConfig = this.companyConfig ? (this.companyConfig.appPages || {}).nluIntentPerformancePage || {} : {};
    let metrics = nluIntentPerformancePageConfig.metrics || {};
    this.showNluMetric = metrics.nlu != null ? metrics.nlu : true;
    this.showFalsePositiveMetric = metrics.falsePositive != null ? metrics.falsePositive : true;
    this.showMsgNotUnderstoodMetric = metrics.notUnderstood != null ? metrics.notUnderstood : true;
    this.showLowQualityIntentsMetric = metrics.lowQualityIntents != null ? metrics.lowQualityIntents : true;

    const panel = this.config.appPanels.find((item) => item.key == 'wy-va-optimization-intent-performance-chart-panel');
    const companyPanel = this.companyConfig.appPanels.find((item) => item.key == 'wy-va-optimization-intent-performance-chart-panel');
    this.showChartPanel = (!panel || panel.enabled) && (companyPanel && companyPanel.enabled) && (this.showNluMetric || this.showFalsePositiveMetric || this.showMsgNotUnderstoodMetric || this.showLowQualityIntentsMetric);

    this.updateDataModelColumns({ columns: this.columns, columnsConfig: nluIntentPerformancePageConfig.columns });

    await this.handleFilterChange({ forceRefresh: false });
  },
  beforeDestroy() {
    this.dataModel.keyword = '';
  },
  destroyed() {
    if (this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', { name: 'optimization_intent_performance_time_spent', data: { time_spent: WyUtils.getTimeDiff(this.initDate) } });
    }
  },
});
</script>

<style lang="scss">
.wy-va-intent-performance-page {
  font-weight: 500;
  font-size: 14px;
  padding-top: 0px;
  .wy-page-nodata-with-available-date-range {
    // margin-top: -10% !important;
    margin-top: 0px !important;
    margin-bottom: 0px;
  }
  .highlight-keywords {
    background-color: rgba(255, 177, 0, 0.22);
  }
  .wy-page-modal {
    height: calc(100% + 1510px);
  }
  .wy-table-list tbody tr td.last-child {
    border-right: 1px solid transparent !important;
  }
  .wy-table-list .wy-table-main tbody tr td.last-child {
    text-align: center !important;
  }
  .wy-table-list .wy-table-main tbody tr td.align-right div {
    padding-right: 8px;
  }
  .wy-table-list .wy-table-main tbody tr td.align-right.last-child div {
    padding-right: 0px;
    padding-left: 10px;
  }

  .wy-table-column-panel {
    top: 188px !important;
    left: 1636px !important;
  }
  .wy-table-column .wy-table-column-dropdown .table-col-dropdown-text-hide {
    display: inline-block;
    color: var(--site-font-tertiary-color);
  }
  .wy-table-list .wy-table-main thead .wy-sub-header th.lastChild {
    text-align: center !important;
  }
  .wy-table-list {
    thead {
      background: #fff;
      span.total-topic-num {
        color: #4b5158 !important;
      }
      .wy-sub-header {
        th.sortable::after {
          // this reserves space for the up-arrow/down-arrow icon (not shown yet)
          content: '';
          display: inline-block;
          height: 15px;
          background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg');
          width: 15px;
          position: relative;
          top: 4px;
          left: 7px;
          background-repeat: no-repeat;
        }
      }
      th.sortable.sorted.ascending::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg');
      }
      th.sortable.sorted.descending::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg');
      }
      th.sortable.sorted.ascending:hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg');
      }
      th.sortable.sorted.descending:hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg');
      }
      th.sortable:hover {
        background-color: #e1e6ec;
        cursor: pointer;
        color: #214586;
        .sort-caret.ascending {
          background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg');
        }
        .sort-caret.descending {
          background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg');
        }
      }
      th.sortable:not(.sorted):hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg');
      }
      th:not(.sortable) {
        span {
          padding-left: 0;
        }
      }
      th.lastChild.fixed.index {
        border-bottom: 1px solid #cad2da;
      }
    }
    tbody tr td {
      color: var(--site-font-tertiary-color);
      font-size: 14.22px;
      font-weight: 400;
    }
    thead .wy-sub-header th.lastChild,
    thead .wy-header th {
      // border-right: 1px solid #CAD2DA;
      //border-bottom: 1px solid #CAD2DA;
      background: #f5f8fa !important;
      color: var(--site-font-secondary-color);
      font-weight: 500;
      font-size: 14.5px;
    }
    .wy-table-list thead .wy-header {
      color: var(--site-font-secondary-color);
      font-weight: 500;
      font-size: 14.5px !important;
    }
    .wy-table-fixed tbody {
      height: calc(100vh - 372px) !important;
      min-height: 366px;
    }
    .wy-table-fixed tbody td:nth-last-child(1) {
      font-weight: 500;
      color: #1a55ad;
      padding-left: 20px !important;
      // background: #fff;
      background-color: transparent;
      font-size: 14.22px;
      // text-decoration: underline;
      padding-right: 12px;
    }
    .wy-table-main thead th {
      //border-bottom: 1px solid #CAD2DA;
      background: #f5f8fa !important;
      color: var(--site-font-secondary-color);
      font-weight: 500;
      font-size: 14.5px;
      text-align: center !important;
    }
    th.wy-table-tail {
      display: none;
    }
  }

  .wy-container-main {
    overflow: unset;
  }

  .wy-table-wrapper {
    width: calc(100% - 3px) !important;
    border-top-left-radius: unset;
    border-top-right-radius: unset;
    min-width: 1063px;
    background-color: #fff !important;
    height: calc(100vh - 250px) !important;
    min-height: 494px;
    margin-bottom: 20px;
    overflow: hidden !important;
    border: 1px solid #cad2da;
    border-radius: 8px;
  }

  .wy-table-main tbody {
    overflow: scroll;
    height: calc(100vh - 350px) !important;
    // min-height: 528px;
    // width: 100%;
    width: calc(100%);
    max-width: 120%;
    min-height: 390px;
    border-right: 2px solid #eee;
    border-bottom: unset;
    &::-webkit-scrollbar {
      width: 20px;
      height: 20px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 150px;
      border: 5px solid transparent;
      border-top: 6px solid transparent;
      background-clip: content-box;
      background-color: #cad2da;
    }
    &::-webkit-scrollbar-track {
      background: #fff;
      border-top: 1px solid #cad2da;
    }
    &::-webkit-scrollbar:vertical {
      width: 14px;
    }
    &::-webkit-scrollbar-thumb:vertical {
      border-radius: 160px;
      border: 2px solid transparent;
      border-left: 4px solid transparent;
      background-clip: content-box;
      background-color: #cad2da;
    }
    &::-webkit-scrollbar-track:vertical {
      border-top: none !important;
      border-left: 1px solid #cad2da;
    }
  }
  .wy-table-main {
    width: 100%;
    max-width: 100%;
  }
  .wy-table-list {
    width: 100%;
    position: relative;
    padding-top: 0px;
    background-color: #fff;

    .wy-table-main,.wy-table-fixed .wy-body {
      tr td.align-right div.el-no-data {
        margin-left: 0px;
        padding-left: 0px !important;
        margin-right: 0px !important;
      }
      tr.disabled-row {
        // pointer-events:none;
        cursor: default;
        td {
          // background-color: #f6f6f6 !important;
          span.row-overflow-style {
            color:var(--site-font-tertiary-color);
            cursor: pointer;
          }
        }
      }
      tr.disabled-row.highlightedRow td {
        // background-color: #f6f6f6 !important;
      }
      td.issuesChangePercent,.volumeChangePercent,.falsePositivesChangePercent,.botRepetitionsChangePercent,.duplicateUtterancesChangePercent,.inconsistentUtterancesChangePercent
      td.issuesChangePercent,.volumeChangePercent,.falsePositivesChangePercent,.botRepetitionsChangePercent,.duplicateUtterancesChangePercent,.inconsistentUtterancesChangePercent {
        .el-no-data {
          padding-left: 0px !important;
        }
      }
    }
    th.lastChild.sortable.fixed.descending.intent {
      text-align: left;
    }
  }
  .wy-va-intent-performance {
    .wy-va-intent-performance-small-title {
      padding: 10px 23px 0px 0px;
      color: var(--site-font-secondary-color);
      font-size: 15px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-weight: 400;
    }
    .annotation-text {
      font-weight: 600;
    }
    .wy-va-intent-performance-title {
      color: var(--site-font-secondary-color);
      font-size: 25px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-weight: 600;
      padding-top: 0px;
      padding: 0px 0px 0px 0px;
      min-width: 1022px;
      display: flex;
      justify-content: space-between;
      height: 40px;
      line-height: 0.7;
      margin-bottom: 22px;
      // background-color: #F4F6F8;
      img.el-tooltip {
        margin-left: 5px;
        width: 18px;
        top: 0px;
        position: relative;
      }
      .export-btn {
        box-sizing: border-box;
        border-radius: 5px;
        background: var(--site-button-secondary-btn-background-primary-color);
        color: var(--site-button-secondary-btn-text-primary-color) !important;
        border: 1px solid var(--site-button-secondary-btn-hover-border-primary-color);
        padding: 6px 15px 9px 11px !important;
        width: 90px;
        font-size: 16px !important;
        font-weight: 500 !important;
        height: 39px;
        float: right;
      }
      .export-btn:hover {
        border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
        background-color: var(--site-button-secondary-btn-hover-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-secondary-btn-text-primary-color) !important;
      }
      .export-btn:active {
        background-color: var(--site-button-secondary-btn-pressed-background-primary-color) !important;
      }
      .export-btn:focus {
        border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-secondary-btn-text-primary-color) !important;
      }
      .export-icon {
        background: var(--icon_action_export);
        height: 17px;
        width: 13px;
        position: relative;
        top: 2px;
        height: 17px;
        width: 13px;
        display: inline-block;
        background-repeat: no-repeat;
      }
      .export-text {
        position: relative;
        bottom: 1px;
        left: 5px;
      }
    }
    .wy-va-opitization-main-content {
      // background-color: #F4F6F8;
      height: unset;
      // margin-right: 49px !important;
      margin-right: 0px !important;
      // padding-top: 26px;

      .wy-va-opitization-chart-section-wrapper {
        // background-color: #F3F6F8;
        background-color: #fff;
      }

      .wy-va-opitization-chart-section {
        box-sizing: border-box;
        padding-top: 0px;
        margin-bottom: 20px;
        display: flex;
        max-height: 363px;
        min-height: 300px;
        width: 100%;
        background-color: #fff;
        border: 1px solid #cad2da;
        border-radius: 5px;
        margin-top: 10px;
        min-width: 1065px;
        align-items: center;
        .optimizationTrendChart {
          .svg-container {
            .main-svg {
              background-color: transparent !important;
              height: 356px;

              .bglayer .bg {
                fill: transparent !important;
              }
            }
          }
        }

        .js-plotly-plot .plotly,
        .js-plotly-plot .plotly div {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        }
        .xtick text,
        .ytick text,
        .legendtext,
        .annotation-text {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
        }
      }
    }

    .wy-optimization-table-section.full-size {
      .wy-table-main tbody {
        width: calc(100% - 1px);
      }
    }

    .wy-optimization-table-section {
      margin-bottom: 50px;
      margin: 0px 0px 20px 0px;
      width: unset;
      min-width: 1065px;
      height: fit-content;
    }

    .optimization-details {
      padding-left: 25px;
      padding-right: 25px;
      width: 45%;
      // height: 223px;
      height: inherit;
      padding-top: 0px;
      // border-top: 1px solid #CAD2DA;
      min-width: 553px;

      .optimization-details-table {
        width: 100%;
        // max-width: 1395px;
        td {
          text-align: center;
          width: 25%;
        }
      }
      .wy-va-optimization-pie-chart-title {
        // padding-bottom: 20px;
        height: 12px;
      }
    }
    .chart.optimizationTrendChart {
      width: 55%;
      min-width: 441px;
      padding-top: 0px;
      // border-top: 1px solid #CAD2DA;
      padding-right: 20px;
    }
    .wy-va-optimization-line-chart {
      padding: 0px;
      margin-bottom: 0px;

      .user-select-none.svg-container {
        height: 270px !important;
        width: 100% !important;

        .main-svg .infolayer .legend {
          transform: translate(56.96px, 235.75px);
        }
      }
    }
  }

  .el-loading-mask {
    opacity: 1;
    height: calc(100vh - 100px) !important;
    margin-top: -5px; // move top to fully hide table header
  }
}
// CSS styles related to svg charts generated by Chartist library from the backend
.ct-area {
  fill-opacity: 0.5;
}
.ct-line {
  stroke-width: 1px;
}
.ct-series-a .ct-area,
.ct-series-a .ct-slice-donut-solid,
.ct-series-a .ct-slice-pie {
  fill: #e6e7f8;
}
.ct-series-a .ct-bar,
.ct-series-a .ct-line,
.ct-series-a .ct-point,
.ct-series-a .ct-slice-donut {
  stroke: #4d50e8;
}
.ct-series-b .ct-area,
.ct-series-b .ct-slice-donut-solid,
.ct-series-b .ct-slice-pie {
  fill: #e8f1ff;
}
.ct-series-b .ct-bar,
.ct-series-b .ct-line,
.ct-series-b .ct-point,
.ct-series-b .ct-slice-donut {
  stroke: #1c4db2;
}
.ct-series-c .ct-area,
.ct-series-c .ct-slice-donut-solid,
.ct-series-c .ct-slice-pie {
  fill: none;
}
.ct-series-c .ct-bar,
.ct-series-c .ct-line,
.ct-series-c .ct-point,
.ct-series-c .ct-slice-donut {
  stroke: black;
  stroke-dasharray: 3px, 3px;
  stroke-width: 0.5px;
}

.el-table .ascending .sort-caret.ascending {
  border-bottom-color: #0C2837;
}

.el-table .descending .sort-caret.descending {
  border-top-color: #0C2837;
}

@media screen and (max-width: 1499px) {
  .wy-va-intent-performance .wy-va-optimization-line-chart {
    .main-svg .infolayer .legend {
      // transform: translate(44.96px, 220.75px) !important;
      transform: translate(44.96px, 191.75px) !important;
    }
  }
}

@media screen and (max-width: 1725px) {
  // .wy-va-optimization-pie-chart .wy-va-optimization-pie-chart-title .el-icon-info.wy-va-optimization-pie-chart-chart-pieMessagesNotUnderstood {
  //   top: 2px;
  //   left: 119px;
  // }
}
</style>
